import PageHero from "@/components/PageHero";
import SectionHeading from "@/components/SectionHeading";
import LocalizedLink from "@/components/LocalizedLink";
import { useLanguage } from "@/i18n/LanguageContext";

const C = {
  ar: {
    eyebrow: "عن المبادرة",
    title: "عن مجتمع الذكاء الاصطناعي السوري",
    lead: "مبادرة تطوّعية غير ربحية تسعى لنشر المعرفة العملية بالذكاء الاصطناعي في سوريا، وتسهيل الوصول إلى أدواته وتطبيقاته لفئات مختلفة من المجتمع.",
    blocks: [
      { title: "من نحن", body: "مجتمع الذكاء الاصطناعي السوري (SAS) هو مبادرة تطوّعية غير ربحية أطلقت عام 2025 لنشر المعرفة العملية بالذكاء الاصطناعي في سوريا، وتسهيل الوصول إلى أدواته وتطبيقاته لفئات مختلفة من المجتمع." },
      { title: "لماذا أطلقنا SAS", body: "جاء إطلاق SAS استجابة لحاجة واضحة إلى محتوى عربي مبسّط، وتوجيه عملي يساعد الطلاب والباحثين والمهتمّين على فهم الذكاء الاصطناعي واستخدامه في التعليم والبحث والعمل والحياة اليومية." },
      { title: "رسالتنا", body: "نشر المعرفة التقنية بلغة بسيطة، وتقديم تدريب مجاني باستخدام أدوات مفتوحة أو مجانية، والوصول إلى كل من يملك الرغبة في التعلم مهما كانت ظروفه." },
      { title: "رؤيتنا", body: "أن يكون SAS من أبرز المبادرات التعليمية المجتمعية في سوريا في مجال الذكاء الاصطناعي، وأن يساهم في بناء بيئة معرفية أكثر وعيًا وقدرة على مواكبة المستقبل." },
    ],
    valuesEyebrow: "قيمنا",
    valuesTitle: "أربع قيم تحكم عملنا اليومي.",
    values: [
      { title: "التمكين", desc: "ندعم الأفراد لاكتساب معرفة عملية تعزّز مسارهم التعليمي والمهني." },
      { title: "البساطة", desc: "نقدّم المفاهيم بلغة واضحة ومباشرة بعيدًا عن التعقيد غير الضروري." },
      { title: "الاعتمادية", desc: "نبني محتوى موثوقًا ومراجع مختارة بعناية مناسبة للسياق العربي والسوري." },
      { title: "العمل الجماعي", desc: "نعمل كفريق تطوّعي متكامل يشارك في التخطيط والتنفيذ والتقييم." },
    ],
    serveTitle: "من نخدم",
    serveBody: "نخدم طلاب الجامعات، والباحثين والمعلمين وطلاب المدارس، والمنظمات، وكل من يرغب في تعلم أدوات الذكاء الاصطناعي والاستفادة منها بشكل عملي.",
    freeTitle: "لماذا الأدوات المجانية",
    freeBody: "نؤمن أن الوصول إلى المعرفة يجب أن يكون متاحًا للجميع، لذلك نركّز على أدوات مجانية أو ذات خطط مجانية عملية تناسب البيئة السورية وتحدّياتها.",
    nextEyebrow: "المرحلة القادمة",
    nextBody: "نطمح إلى توسيع البرامج داخل الكليات، وبناء مسار تعليمي أكثر تكاملًا، وتحويل SAS من مبادرة توعوية إلى بيئة تعليمية عملية تدعم الطالب والباحث والمجتمع.",
    ctaTitle: "دعوة للتعاون",
    ctaDesc: "هل تمثل جامعة أو مدرسة أو منظمة أو مركزًا تدريبيًا؟ يسعدنا التعاون معكم لتنظيم ورشات تعريفية أو تطبيقية في مجال الذكاء الاصطناعي.",
    ctaBtn: "تواصل معنا",
  },
  en: {
    eyebrow: "About the initiative",
    title: "About the Syrian AI Society",
    lead: "A non-profit volunteer initiative dedicated to spreading practical AI knowledge in Syria and making its tools and applications accessible to diverse audiences.",
    blocks: [
      { title: "Who we are", body: "The Syrian AI Society (SAS) is a non-profit volunteer initiative launched in 2025 to spread practical AI knowledge in Syria and make AI tools and applications accessible to diverse audiences." },
      { title: "Why we launched SAS", body: "SAS was launched in response to a clear need for simplified Arabic content and practical guidance that helps students, researchers, and curious learners understand AI and apply it in study, research, work, and daily life." },
      { title: "Our mission", body: "To spread technical knowledge in simple language, provide free training using open or free tools, and reach everyone willing to learn — whatever their circumstances." },
      { title: "Our vision", body: "That SAS becomes one of the most notable community-education initiatives in Syria in the AI field, contributing to a more aware and future-ready knowledge environment." },
    ],
    valuesEyebrow: "Our values",
    valuesTitle: "Four values that guide our daily work.",
    values: [
      { title: "Empowerment", desc: "We help individuals gain practical knowledge that strengthens their academic and professional path." },
      { title: "Simplicity", desc: "We present concepts in clear, direct language — away from unnecessary complexity." },
      { title: "Reliability", desc: "We curate trustworthy content and references chosen with care for the Arabic and Syrian context." },
      { title: "Collaboration", desc: "We work as an integrated volunteer team sharing planning, execution, and evaluation." },
    ],
    serveTitle: "Who we serve",
    serveBody: "We serve university students, researchers, educators, school students, organisations, and anyone wishing to learn AI tools and benefit from them practically.",
    freeTitle: "Why free tools",
    freeBody: "We believe access to knowledge should be available to everyone, so we focus on free tools — or ones with practical free tiers — that fit the Syrian context and its constraints.",
    nextEyebrow: "Next phase",
    nextBody: "We aim to expand programmes inside faculties, build a more integrated learning pathway, and evolve SAS from an awareness initiative into a practical educational environment that supports students, researchers, and the community.",
    ctaTitle: "An invitation to collaborate",
    ctaDesc: "Do you represent a university, school, organisation, or training centre? We'd be glad to collaborate on awareness or practical AI workshops.",
    ctaBtn: "Get in touch",
  },
};

export default function About() {
  const { lang } = useLanguage();
  const c = C[lang];

  return (
    <>
      <PageHero eyebrow={c.eyebrow} title={c.title} lead={c.lead} />

      <section className="sas-section" data-testid="about-blocks">
        <div className="sas-container grid md:grid-cols-2 gap-y-10 gap-x-12">
          {c.blocks.map((b) => (
            <div key={b.title}>
              <h2 className="sas-h3">{b.title}</h2>
              <p className="sas-body mt-3 text-[16px] leading-[2]">{b.body}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="sas-divider" />

      <section className="sas-section" data-testid="about-values">
        <div className="sas-container">
          <SectionHeading eyebrow={c.valuesEyebrow} title={c.valuesTitle} />
          <div className="sas-grid-4 mt-10">
            {c.values.map((v, i) => (
              <div key={v.title} className="sas-card">
                <div className="text-[color:var(--sas-accent)] font-semibold text-sm mb-3">0{i + 1}</div>
                <h3 className="sas-h3">{v.title}</h3>
                <p className="sas-body mt-3 text-[15px]">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="sas-divider" />

      <section className="sas-section" data-testid="about-who-serve">
        <div className="sas-container grid md:grid-cols-2 gap-y-10 gap-x-12">
          <div>
            <h2 className="sas-h3">{c.serveTitle}</h2>
            <p className="sas-body mt-3 text-[16px] leading-[2]">{c.serveBody}</p>
          </div>
          <div>
            <h2 className="sas-h3">{c.freeTitle}</h2>
            <p className="sas-body mt-3 text-[16px] leading-[2]">{c.freeBody}</p>
          </div>
        </div>
      </section>

      <div className="sas-divider" />

      <section className="sas-section" data-testid="about-vision-next">
        <div className="sas-container max-w-4xl">
          <div className="sas-eyebrow mb-4">{c.nextEyebrow}</div>
          <p className="sas-h2">{c.nextBody}</p>
        </div>
      </section>

      <section className="sas-section-sm bg-[color:var(--sas-accent-soft)] border-y border-[color:var(--sas-line)]">
        <div className="sas-container">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div className="max-w-2xl">
              <h3 className="sas-h2">{c.ctaTitle}</h3>
              <p className="sas-body mt-3">{c.ctaDesc}</p>
            </div>
            <LocalizedLink to="/contact" className="sas-btn sas-btn-primary">{c.ctaBtn}</LocalizedLink>
          </div>
        </div>
      </section>
    </>
  );
}
